import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FileText, Plus, Save, Trash2 } from 'lucide-react'
import toast from 'react-hot-toast'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { showSaveSuccess, showSaveError } from '@/lib/swal'
import { useAuthStore } from '@/store/auth.store'

import {
  scPurchaseOrderService,
  type ScPurchaseOrderLine,
  type ScPurchaseOrderMaster,
  type ScRequisitionOption,
  type ScTaxOption,
} from '@/services/scPurchaseOrderService'

function todayIso() {
  return new Date().toISOString().slice(0, 10)
}

function newLine(defaultTax?: ScTaxOption): ScPurchaseOrderLine {
  return {
    id: 0,
    orderId: 0,
    itemId: 0,
    itemName: '',
    description: '',
    quantity: 0,
    unitPrice: 0,
    discountPercent: 0,
    discount: 0,
    discountAmount: 0,
    taxableAmount: 0,
    tax: defaultTax?.id ?? 0,
    taxPercent: defaultTax?.percent ?? 0,
    taxAmount: 0,
    lineAmount: 0,
    isDeleted: 'false',
  }
}

export const SupplyChainPurchaseOrderEdit: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isEdit = Boolean(id && id !== 'new')
  const branchId = useAuthStore((s) => s.user?.selectedBranch?.branchId) ?? 0
  const userId = useAuthStore((s) => s.user?.userId) ?? 0

  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(false)

  const [requisitions, setRequisitions] = useState<ScRequisitionOption[]>([])
  const [vendors, setVendors] = useState<Array<{ id: number; name: string }>>([])
  const [taxes, setTaxes] = useState<ScTaxOption[]>([])
  const [paymentTerms, setPaymentTerms] = useState<Array<{ id: number; name: string }>>([])

  const [master, setMaster] = useState<ScPurchaseOrderMaster>({
    id: 0,
    code: '',
    requisitionId: 0,
    requisitionCode: '',
    vendorId: 0,
    vendorName: '',
    date: todayIso(),
    branchId,
    reference: '',
    estimatedDeliveryDate: todayIso(),
    paymentTerms: 0,
    paymentTermsName: '',
    attachmentPath: '',
    taxableAmount: 0,
    taxAmount: 0,
    discount: 0,
    total: 0,
    status: 0,
    createdDate: undefined,
    createdBy: undefined,
    updatedDate: undefined,
    updatedById: undefined,
  })

  const [attachmentFile, setAttachmentFile] = useState<File | null>(null)
  const [lines, setLines] = useState<ScPurchaseOrderLine[]>([])

  const defaultTax = useMemo(() => {
    const byId = taxes.find((x) => x.id === 1)
    if (byId) return byId
    const by15 = taxes.find((x) => Math.abs((x.percent || 0) - 15) < 0.01)
    return by15 ?? null
  }, [taxes])

  const vendorOptions = useMemo(
    () => vendors.map((v) => ({ value: String(v.id), label: v.name })),
    [vendors],
  )
  const requisitionOptions = useMemo(
    () => requisitions.map((r) => ({ value: String(r.id), label: r.code })),
    [requisitions],
  )
  const taxOptions = useMemo(
    () => taxes.map((x) => ({ value: String(x.id), label: `${x.name} (${x.percent}%)` })),
    [taxes],
  )
  const paymentTermOptions = useMemo(
    () => paymentTerms.map((x) => ({ value: String(x.id), label: x.name })),
    [paymentTerms],
  )

  useEffect(() => {
    setMaster((p) => ({ ...p, branchId }))
  }, [branchId])

  useEffect(() => {
    if (!branchId) return
    const loadLookups = async () => {
      try {
        const [reqs, vends, tx, terms] = await Promise.all([
          scPurchaseOrderService.getRequisitions(branchId),
          scPurchaseOrderService.getVendors(branchId),
          scPurchaseOrderService.getTaxes(branchId),
          scPurchaseOrderService.getPaymentTerms(),
        ])
        setRequisitions(reqs)
        setVendors(vends)
        setTaxes(tx)
        setPaymentTerms(terms)
      } catch (error: any) {
        toast.error(error?.response?.data?.message || t('common.failed'))
        setRequisitions([])
        setVendors([])
        setTaxes([])
        setPaymentTerms([])
      }
    }
    void loadLookups()
  }, [branchId, t])

  useEffect(() => {
    if (!isEdit || !id || !branchId) return
    const loadForEdit = async () => {
      try {
        setLoading(true)
        const numericId = Number(id)
        const [m, l] = await Promise.all([
          scPurchaseOrderService.getById(numericId, branchId),
          scPurchaseOrderService.getLinesByOrderId(numericId, branchId),
        ])
        setMaster(m)
        setLines(l.length ? l : [newLine(defaultTax || undefined)])
      } catch (error: any) {
        toast.error(error?.response?.data?.message || t('purchaseOrders.failedToLoadForEdit'))
        navigate('/supply-chain/purchase-orders')
      } finally {
        setLoading(false)
      }
    }
    void loadForEdit()
  }, [branchId, defaultTax, id, isEdit, navigate, t])

  // Ensure the edit record's requisition is present in the dropdown options.
  // Some APIs return only a subset of requisitions; without this, SearchableSelect shows placeholder.
  useEffect(() => {
    const rid = Number(master.requisitionId || 0)
    const code = String(master.requisitionCode || '').trim()
    if (!rid || !code) return
    setRequisitions((prev) => {
      if (prev.some((x) => x.id === rid)) return prev
      return [{ id: rid, code }, ...prev]
    })
  }, [master.requisitionCode, master.requisitionId])

  useEffect(() => {
    if (isEdit) return
    // initialize new order lines after taxes load (default tax)
    setLines((prev) => (prev.length ? prev : Array.from({ length: 3 }, () => newLine(defaultTax || undefined))))
  }, [defaultTax, isEdit])

  const computeLineAmounts = (line: ScPurchaseOrderLine) => {
    const qty = Number(line.quantity || 0)
    const unitPrice = Number(line.unitPrice || 0)
    const sub = qty * unitPrice
    const discPercent = 0
    const discAmount = 0
    const taxable = sub
    const taxPercent = Number(line.taxPercent || 0)
    const taxAmount = taxable * (taxPercent / 100)
    const total = taxable + taxAmount
    return {
      discountAmount: Number(discAmount.toFixed(2)),
      taxableAmount: Number(taxable.toFixed(2)),
      taxAmount: Number(taxAmount.toFixed(2)),
      lineAmount: Number(total.toFixed(2)),
      discount: Number(discPercent.toFixed(2)),
    }
  }

  const totals = useMemo(() => {
    const active = lines.filter((l) => String(l.isDeleted || 'false').toLowerCase() !== 'true')
    const subTotal = active.reduce((sum, l) => sum + Number(l.quantity || 0) * Number(l.unitPrice || 0), 0)
    const discount = active.reduce((sum, l) => sum + Number(l.discountAmount || 0), 0)
    const taxable = active.reduce((sum, l) => sum + Number(l.taxableAmount || 0), 0)
    const tax = active.reduce((sum, l) => sum + Number(l.taxAmount || 0), 0)
    const net = active.reduce((sum, l) => sum + Number(l.lineAmount || 0), 0)
    return { subTotal, discount, taxable, tax, net }
  }, [lines])

  const updateLine = (idx: number, patch: Partial<ScPurchaseOrderLine>) => {
    setLines((prev) =>
      prev.map((l, i) => {
        if (i !== idx) return l
        const next = { ...l, ...patch }
        const computed = computeLineAmounts(next)
        return {
          ...next,
          discount: computed.discount,
          discountAmount: computed.discountAmount,
          taxableAmount: computed.taxableAmount,
          taxAmount: computed.taxAmount,
          lineAmount: computed.lineAmount,
        }
      }),
    )
  }

  const addRows = (count: number) => setLines((prev) => [...prev, ...Array.from({ length: count }, () => newLine(defaultTax || undefined))])

  const removeLine = (idx: number) => setLines((prev) => prev.filter((_, i) => i !== idx))

  const applyRequisition = async (requisitionId: number) => {
    if (!branchId || requisitionId <= 0) return
    try {
      setLoading(true)
      const details = await scPurchaseOrderService.getRequisitionById(requisitionId, branchId)
      const reqCode = requisitions.find((r) => r.id === requisitionId)?.code ?? ''
      setMaster((p) => ({
        ...p,
        requisitionId,
        requisitionCode: reqCode,
      }))
      setLines(
        details.map((d) => {
          const base: ScPurchaseOrderLine = {
            ...newLine(defaultTax || undefined),
            itemId: d.itemId,
            itemName: d.itemName,
            quantity: d.quantity,
            // readonly from requisition
            unitPrice: 0,
            discountPercent: 0,
            description: '',
          }
          const computed = computeLineAmounts(base)
          return {
            ...base,
            discount: computed.discount,
            discountAmount: computed.discountAmount,
            taxableAmount: computed.taxableAmount,
            taxAmount: computed.taxAmount,
            lineAmount: computed.lineAmount,
          }
        }),
      )
    } catch (error: any) {
      toast.error(error?.response?.data?.message || t('common.failed'))
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    if (!branchId) {
      toast.error(t('purchaseOrders.pleaseSelectBranch'))
      return
    }
    if (!master.requisitionId) {
      toast.error(t('purchaseOrders.failedToLoadDropdowns'))
      return
    }
    if (!master.vendorId) {
      toast.error(t('purchaseOrders.pleaseSelectVendor'))
      return
    }

    const validLines = (lines || [])
      .map((l) => ({ ...l }))
      .filter((l) => Number(l.itemId || 0) > 0 && Number(l.quantity || 0) > 0 && String(l.isDeleted || 'false').toLowerCase() !== 'true')

    if (validLines.length === 0) {
      toast.error(t('purchaseOrders.atLeastOneLine'))
      return
    }

    try {
      setSaving(true)
      const vendorName = vendors.find((v) => v.id === master.vendorId)?.name ?? master.vendorName ?? ''
      const resolvedReqCode = master.requisitionCode || requisitions.find((r) => r.id === master.requisitionId)?.code || ''
      const now = new Date().toISOString()
      const resolvedCode = String(master.code || '').trim() || resolvedReqCode.trim() || String(master.reference || '').trim() || `SCPO-${Date.now()}`

      const payload = {
        ...master,
        id: isEdit ? master.id : 0,
        code: resolvedCode,
        branchId,
        requisitionCode: resolvedReqCode,
        vendorName,
        taxableAmount: Number(totals.taxable.toFixed(2)),
        taxAmount: Number(totals.tax.toFixed(2)),
        discount: 0,
        total: Number(totals.net.toFixed(2)),
        createdDate: isEdit ? master.createdDate : now,
        createdBy: isEdit ? master.createdBy : userId,
        updatedDate: now,
        updatedById: userId,
        attachmentFile,
        lines: validLines.map((l) => ({
          ...l,
          orderId: isEdit ? master.id : 0,
          itemName: l.itemName || '',
          description: l.description || '',
          isDeleted: l.isDeleted || 'false',
        })),
      }

      if (isEdit) await scPurchaseOrderService.update(payload)
      else await scPurchaseOrderService.save(payload)

      await showSaveSuccess(isEdit, isEdit ? t('purchaseOrders.updatedSuccess') : t('purchaseOrders.savedSuccess'))
      navigate('/supply-chain/purchase-orders')
    } catch (error: any) {
      await showSaveError(error, isEdit)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <FileText className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('purchaseOrders.title')}</h1>
          <p className="text-sm text-muted-foreground">
            {isEdit ? t('purchaseOrders.subtitleEdit') : t('purchaseOrders.subtitleNew')}
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('purchaseOrders.orderDetails')}</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="space-y-2">
            <Label>{t('purchaseOrders.orderDate')}</Label>
            <Input
              type="date"
              value={master.date}
              onChange={(e) => setMaster((p) => ({ ...p, date: e.target.value }))}
              disabled={saving || loading}
            />
          </div>
          <div className="space-y-2">
            <Label>{t('purchaseOrders.requisitionCode')} *</Label>
            <SearchableSelect
              options={requisitionOptions}
              value={String(master.requisitionId || '')}
              onValueChange={(v) => {
                const rid = Number(v || 0)
                void applyRequisition(rid)
              }}
              placeholder={t('purchaseOrders.selectRequisition')}
              searchPlaceholder={t('purchaseRequisitions.searchPlaceholder')}
              emptyMessage={t('common.noData')}
              disabled={saving || loading}
            />
          </div>
          <div className="space-y-2">
            <Label>{t('purchaseOrders.reference')}</Label>
            <Input
              value={master.reference}
              onChange={(e) => setMaster((p) => ({ ...p, reference: e.target.value }))}
              placeholder={t('purchaseOrders.placeholderReference')}
              disabled={saving || loading}
            />
          </div>
          <div className="space-y-2">
            <Label>{t('purchaseOrders.vendors')}</Label>
            <SearchableSelect
              options={vendorOptions}
              value={String(master.vendorId || '')}
              onValueChange={(v) => setMaster((p) => ({ ...p, vendorId: Number(v || 0) }))}
              placeholder={t('purchaseOrders.selectVendor')}
              searchPlaceholder={t('purchaseRequisitions.searchPlaceholder')}
              emptyMessage={t('common.noData')}
              disabled={saving || loading}
            />
          </div>
          <div className="space-y-2">
            <Label>{t('purchaseOrders.estimatedDeliveryDate')}</Label>
            <Input
              type="date"
              value={master.estimatedDeliveryDate}
              onChange={(e) => setMaster((p) => ({ ...p, estimatedDeliveryDate: e.target.value }))}
              disabled={saving || loading}
            />
          </div>
          <div className="space-y-2">
            <Label>{t('purchaseOrders.paymentTerms')}</Label>
            <SearchableSelect
              options={paymentTermOptions}
              value={String(master.paymentTerms || '')}
              onValueChange={(v) => {
                const termId = Number(v || 0)
                const termName = paymentTerms.find((x) => x.id === termId)?.name ?? ''
                setMaster((p) => ({ ...p, paymentTerms: termId, paymentTermsName: termName }))
              }}
              placeholder={t('purchaseOrders.selectPaymentTerms')}
              searchPlaceholder={t('purchaseRequisitions.searchPlaceholder')}
              emptyMessage={t('common.noData')}
              disabled={saving || loading}
            />
          </div>
          <div className="space-y-2 md:col-span-3">
            <Label>{t('purchaseOrders.file')}</Label>
            <Input
              type="file"
              onChange={(e) => setAttachmentFile(e.target.files?.[0] || null)}
              disabled={saving || loading}
            />
            {attachmentFile ? (
              <p className="text-xs text-muted-foreground">{t('purchaseOrders.selectedFile', { name: attachmentFile.name })}</p>
            ) : (
              <p className="text-xs text-muted-foreground">{t('purchaseRequisitions.noFileChosen')}</p>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>{t('purchaseOrders.orderItems')}</CardTitle>
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => addRows(1)} disabled={saving}>
              <Plus className="mr-1 h-4 w-4" /> {t('purchaseOrders.addRow')}
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => addRows(3)} disabled={saving}>
              {t('purchaseOrders.add3Rows')}
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => addRows(5)} disabled={saving}>
              {t('purchaseOrders.add5Rows')}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full min-w-[860px]">
            <thead>
              <tr className="border-b bg-primary/5">
                <th className="p-2 text-left text-xs font-semibold">{t('purchaseOrders.item')}</th>
                <th className="p-2 text-left text-xs font-semibold">{t('purchaseOrders.quantity')}</th>
                <th className="p-2 text-left text-xs font-semibold">{t('purchaseOrders.unitPrice')}</th>
                <th className="p-2 text-left text-xs font-semibold">{t('purchaseOrders.taxableAmount')}</th>
                <th className="p-2 text-left text-xs font-semibold">{t('purchaseOrders.tax')}</th>
                <th className="p-2 text-left text-xs font-semibold">{t('purchaseOrders.taxAmount')}</th>
                <th className="p-2 text-left text-xs font-semibold">{t('purchaseOrders.total')}</th>
                <th className="p-2 w-[60px]"></th>
              </tr>
            </thead>
            <tbody>
              {(lines || []).map((line, idx) => {
                return (
                  <tr key={`${line.id}-${idx}`} className="border-b">
                    <td className="p-2">
                      <Input readOnly value={line.itemName || ''} placeholder={t('purchaseOrders.selectItem')} />
                    </td>
                    <td className="p-2">
                      <Input
                        type="number"
                        min={0}
                        value={line.quantity || 0}
                        readOnly
                      />
                    </td>
                    <td className="p-2">
                      <Input
                        type="number"
                        min={0}
                        value={line.unitPrice || ''}
                        onChange={(e) => updateLine(idx, { unitPrice: Number(e.target.value) || 0 })}
                        disabled={saving || loading}
                      />
                    </td>
                    <td className="p-2">
                      <Input readOnly value={Number(line.taxableAmount || 0).toFixed(2)} />
                    </td>
                    <td className="p-2">
                      <SearchableSelect
                        options={taxOptions}
                        value={String(line.tax || '')}
                        onValueChange={(v) => {
                          const taxId = Number(v || 0)
                          const tax = taxes.find((x) => x.id === taxId)
                          updateLine(idx, { tax: taxId, taxPercent: tax?.percent || 0 })
                        }}
                        placeholder={t('purchaseOrders.selectTax')}
                        searchPlaceholder={t('purchaseRequisitions.searchPlaceholder')}
                        emptyMessage={t('common.noData')}
                        disabled={saving || loading}
                      />
                    </td>
                    <td className="p-2">
                      <Input readOnly value={Number(line.taxAmount || 0).toFixed(2)} />
                    </td>
                    <td className="p-2">
                      <Input readOnly value={Number(line.lineAmount || 0).toFixed(2)} />
                    </td>
                    <td className="p-2 text-center">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeLine(idx)}
                        className="text-destructive hover:bg-destructive/10"
                        title={t('purchaseOrders.deleteRow')}
                        disabled={saving || loading}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>

          <div className="mt-6 rounded-md bg-primary/5 p-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <Label className="text-sm text-muted-foreground">{t('purchaseOrders.subTotal')}</Label>
                <p className="text-lg font-semibold">{Number(totals.subTotal || 0).toFixed(2)}</p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">{t('purchaseOrders.totalTax')}</Label>
                <p className="text-lg font-semibold">{Number(totals.tax || 0).toFixed(2)}</p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">{t('purchaseOrders.netAmount')}</Label>
                <p className="text-lg font-semibold">{Number(totals.net || 0).toFixed(2)}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => navigate('/supply-chain/purchase-orders')} disabled={saving}>
          {t('common.cancel')}
        </Button>
        <Button onClick={handleSave} disabled={saving}>
          <Save className="mr-2 h-4 w-4" />
          {isEdit ? t('common.update') : t('purchaseOrders.save')}
        </Button>
      </div>
    </div>
  )
}

